import React, { useCallback, useRef, useState } from "react";
import api from "../../store/services/api";

// MUI
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, keyframes, styled } from "@mui/material/styles";

// Icons
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ─── Animations ──────────────────────────────────────────────────────────

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled Components ───────────────────────────────────────────────────

const ModalShell = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxHeight: "92vh",
  backgroundColor: theme.palette.background.default,
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 25px 60px -12px rgba(15, 23, 42, 0.25)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&:focus-visible": { outline: "none" },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "100%",
    maxHeight: "100vh",
    borderRadius: 0,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.grey[400], 0.35),
    borderRadius: 2,
  },
}));

const FormatGuide = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 6,
  overflow: "hidden",
}));

const FormatGuideHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.75),
  backgroundColor: alpha(theme.palette.info.main, 0.06),
  borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
}));

const ColumnsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
});

const ColumnCell = styled(Box)(({ theme, isLast }) => ({
  padding: theme.spacing(1, 1.25),
  display: "flex",
  flexDirection: "column",
  gap: 2,
  borderRight: isLast ? "none" : `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ExampleCell = styled(Box)(({ theme, isLast }) => ({
  padding: theme.spacing(0.75, 1.25),
  borderRight: isLast ? "none" : `1px solid ${theme.palette.divider}`,
}));

const RequiredBadge = styled("span")(({ theme }) => ({
  fontSize: "0.58rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.palette.error.main,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  padding: "1px 5px",
  borderRadius: 3,
  display: "inline-block",
  lineHeight: 1.5,
  width: "fit-content",
}));

const DropZone = styled(Box, {
  shouldForwardProp: (p) =>
    p !== "isDragging" && p !== "hasFile" && p !== "hasError",
})(({ theme, isDragging, hasFile, hasError }) => {
  const base = {
    borderRadius: 6,
    border: `2px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3.5, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
    minHeight: 140,
    "&:hover": {
      borderColor: alpha(theme.palette.primary.main, 0.4),
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
  };

  if (isDragging) {
    return {
      ...base,
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      "& .drop-icon": {
        color: theme.palette.primary.main,
        transform: "scale(1.12) translateY(-2px)",
      },
    };
  }

  if (hasError) {
    return {
      ...base,
      borderColor: alpha(theme.palette.error.main, 0.4),
      backgroundColor: alpha(theme.palette.error.main, 0.03),
    };
  }

  if (hasFile) {
    return {
      ...base,
      borderColor: alpha(theme.palette.success.main, 0.4),
      backgroundColor: alpha(theme.palette.success.main, 0.03),
      borderStyle: "solid",
      cursor: "default",
      padding: theme.spacing(2.5, 2),
      minHeight: "auto",
      "&:hover": {
        borderColor: alpha(theme.palette.success.main, 0.4),
        backgroundColor: alpha(theme.palette.success.main, 0.03),
      },
    };
  }

  return base;
});

const FileCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  width: "100%",
  animation: `${fadeSlideUp} 0.25s ease-out both`,
});

const FileIconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.dark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.error.main, 0.06),
  border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
  animation: `${fadeSlideUp} 0.2s ease-out both`,
}));

const Footer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

// ─── Helpers ─────────────────────────────────────────────────────────────

const COLUMNS = [
  { name: "Nome", required: true, example: "Daniel Carneiro" },
  { name: "Celular", required: true, example: "(99) 99999-9999" },
  { name: "Endereço", required: false, example: "Rua Joaquim Nelson" },
  { name: "Num", required: false, example: "22" },
  { name: "Bairro", required: false, example: "Centro" },
];

const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];

function isValidFile(file) {
  return ACCEPTED_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// ─── Component ───────────────────────────────────────────────────────────

export default function ImportCustomersExcelFileModal({
  show,
  closeModal,
  loadCustomers,
}) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!isValidFile(file)) {
      setError("Formato inválido. Use arquivos .xlsx ou .csv");
      return;
    }

    setSelectedFile(file);
    setError(null);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || uploading) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      await api.post("/customers/import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });
      loadCustomers();
      closeModal();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Erro ao importar arquivo. Tente novamente."
      );
    } finally {
      setUploading(false);
    }
  }, [selectedFile, uploading, loadCustomers, closeModal]);

  const handleZoneClick = useCallback(() => {
    if (!selectedFile && !uploading) {
      fileInputRef.current?.click();
    }
  }, [selectedFile, uploading]);

  return (
    <Modal
      open={show}
      onClose={uploading ? undefined : closeModal}
      closeAfterTransition
      sx={{ zIndex: 5000, "&:focus-visible": { outline: "none" } }}
      BackdropProps={{
        timeout: 500,
        sx: { backgroundColor: "rgba(15, 23, 42, 0.5)" },
      }}
    >
      <Fade in={show}>
        <ModalShell>
          {/* ── Header ──────────────────────────────────────────────── */}
          <Header>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={(theme) => ({
                  width: 32,
                  height: 32,
                  borderRadius: "6px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                })}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.3,
                  }}
                >
                  Importar Clientes
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                  }}
                >
                  Arquivos .xlsx ou .csv
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={closeModal}
              disabled={uploading}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Header>

          {/* ── Upload progress bar ─────────────────────────────────── */}
          {uploading && (
            <LinearProgress
              variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
              value={uploadProgress}
              sx={{ height: 2, flexShrink: 0 }}
            />
          )}

          {/* ── Body ────────────────────────────────────────────────── */}
          <Body>
            {/* Format guide */}
            <FormatGuide>
              <FormatGuideHeader>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "info.main" }} />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "info.dark",
                  }}
                >
                  Formato esperado da planilha
                </Typography>
              </FormatGuideHeader>

              {/* Column headers */}
              <ColumnsGrid>
                {COLUMNS.map((col, i) => (
                  <ColumnCell key={col.name} isLast={i === COLUMNS.length - 1}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: "text.primary",
                        lineHeight: 1.2,
                      }}
                    >
                      {col.name}
                    </Typography>
                    {col.required && <RequiredBadge>Obrigatório</RequiredBadge>}
                  </ColumnCell>
                ))}
              </ColumnsGrid>

              {/* Example row */}
              <ColumnsGrid>
                {COLUMNS.map((col, i) => (
                  <ExampleCell key={col.name} isLast={i === COLUMNS.length - 1}>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={col.example}
                    >
                      {col.example}
                    </Typography>
                  </ExampleCell>
                ))}
              </ColumnsGrid>
            </FormatGuide>

            {/* Drop zone */}
            <input
              accept=".xlsx,.csv"
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            <DropZone
              isDragging={isDragging}
              hasFile={!!selectedFile}
              hasError={!!error && !selectedFile}
              onClick={handleZoneClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <FileCard>
                  <FileIconBox>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
                  </FileIconBox>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                    >
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    disabled={uploading}
                    sx={(theme) => ({
                      color: theme.palette.grey[400],
                      "&:hover": { color: theme.palette.error.main },
                    })}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </FileCard>
              ) : (
                <>
                  <CloudUploadOutlinedIcon
                    className="drop-icon"
                    sx={(theme) => ({
                      fontSize: 36,
                      color: theme.palette.grey[400],
                      transition: "all 0.2s ease",
                    })}
                  />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 0.25,
                      }}
                    >
                      Arraste o arquivo ou clique para selecionar
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.72rem", color: "text.secondary" }}
                    >
                      Formatos aceitos: .xlsx, .csv
                    </Typography>
                  </Box>
                </>
              )}
            </DropZone>

            {/* Error display */}
            {error && (
              <ErrorBox>
                <ErrorOutlineIcon
                  sx={{ fontSize: 18, color: "error.main", mt: "1px" }}
                />
                <Typography
                  sx={{ fontSize: "0.78rem", color: "error.dark", flex: 1 }}
                >
                  {error}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setError(null)}
                  sx={{ color: "error.main", p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </ErrorBox>
            )}
          </Body>

          {/* ── Footer ──────────────────────────────────────────────── */}
          <Footer>
            <Button
              onClick={closeModal}
              disabled={uploading}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "none",
                px: 2,
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                textTransform: "none",
                px: 3,
                borderRadius: "6px",
              }}
            >
              {uploading
                ? uploadProgress > 0
                  ? `Importando ${uploadProgress}%`
                  : "Importando..."
                : "Importar"}
            </Button>
          </Footer>
        </ModalShell>
      </Fade>
    </Modal>
  );
}
